import * as React from 'react';
import type { Facet } from '../../Search/Facet.csharp';
import type { SearchCriteria } from '../../Search/SearchCriteria.csharp';
import type { SelectedFacet } from '../../Search/SelectedFacet.csharp';
import { getBaseSearchQueryParameters, phraseParameterName } from '../PagedFetchData/SearchCriteriaUtils';
import { FacetsQueryParameterConverter } from './FacetsQueryParameterConverter';
import { changeQueryParametersInActiveUrl, getQueryParametersFromUrl } from './QueryParameterUtils';

interface UseSearchBaseProps {
    phrase: string;
    facets: Facet[];
    selectedFacets?: SelectedFacet[];
    initialSearchCriteria?: SearchCriteria;
}

interface UseSearchBaseReturn {
    searchParams: SearchCriteria;
    handleQueryChange: (newSearchValue: string) => void;
    handleFacetChange: (selectedFacets: SelectedFacet[]) => void;
    handleSearchParamsChange: (searchCriteria: SearchCriteria) => void;
    getBaseSearchQueryParameters: (searchCriteria: SearchCriteria) => Array<{ name: string; value: string }>;
}

const initSelectedFacetState = (facets: Facet[], selectedFacets: SelectedFacet[]): SelectedFacet[] =>
    facets.map(facet => {
        const matchedInBothCollectionSelectedFacets = selectedFacets.filter(
            selectedFacet => selectedFacet.id === facet.id
        );

        if (matchedInBothCollectionSelectedFacets.length === 1) {
            return matchedInBothCollectionSelectedFacets[0];
        }

        return {
            id: facet.id,
            values: [] as string[],
        };
    });

export const useSearchBase = (props: UseSearchBaseProps): UseSearchBaseReturn => {
    const { phrase, facets, selectedFacets = [] } = props;

    const [searchParams, setSearchParams] = React.useState<SearchCriteria>(() => ({
        phrase: phrase || '',
        selectedFacets: initSelectedFacetState(facets, selectedFacets),
    }));

    const queryParameterConverter = React.useMemo(() => new FacetsQueryParameterConverter(facets), [facets]);

    React.useEffect(() => {
        const queryParams = getQueryParametersFromUrl(document.URL);

        if (queryParams.length === 0) {
            return undefined;
        }

        const phraseParam = queryParams.find(queryParameter => queryParameter.name === phraseParameterName);
        const phraseInUrl = phraseParam && phraseParam.value;
        const facetParams = queryParams.filter(queryParameter => queryParameter.name !== phraseParameterName);

        const selectedFacetsResult: SelectedFacet[] = facetParams.reduce((result: SelectedFacet[], item) => {
            const newItem = queryParameterConverter.convertFromUserReadableQueryParameter(item);
            const { name, value } = newItem;
            const facet = result.find(selectedFacet => selectedFacet.id === name);

            if (facet) {
                facet.values = [...facet.values, value];
            } else {
                result.push({ id: name, values: [value] });
            }

            return result;
        }, []);
        const emptyFacets = searchParams.selectedFacets.filter(
            item => selectedFacetsResult.map(selectedFacet => selectedFacet.id).indexOf(item.id) === -1
        );

        const newSearchParams = {
            phrase: phraseInUrl || '',
            selectedFacets: selectedFacetsResult.concat(emptyFacets),
        };

        if (!newSearchParams.phrase && !newSearchParams.selectedFacets.length) {
            return;
        }

        if (newSearchParams.selectedFacets.length) {
            setSearchParams(newSearchParams);
        } else if (newSearchParams.phrase) {
            setSearchParams(prev => ({ ...prev, phrase: newSearchParams.phrase }));
        }
    }, []);

    React.useEffect(() => {
        const queryParams = getBaseSearchQueryParameters(searchParams);
        const translatedQueryParams = queryParams.map(param =>
            queryParameterConverter.convertToUserReadableQueryParameter(param)
        );

        changeQueryParametersInActiveUrl(translatedQueryParams, false);
    }, [searchParams, queryParameterConverter]);

    const handleQueryChange = React.useCallback((newSearchValue: string) => {
        setSearchParams(prev => ({ ...prev, phrase: newSearchValue }));
    }, []);

    const handleFacetChange = React.useCallback((newSelectedFacets: SelectedFacet[]) => {
        setSearchParams(prev => ({ ...prev, selectedFacets: newSelectedFacets }));
    }, []);

    const handleSearchParamsChange = React.useCallback((searchCriteria: SearchCriteria) => {
        setSearchParams(searchCriteria);
    }, []);

    const getBaseSearchQueryParametersCallback = React.useCallback(
        (searchCriteria: SearchCriteria) => getBaseSearchQueryParameters(searchCriteria),
        []
    );

    return {
        searchParams,
        handleQueryChange,
        handleFacetChange,
        handleSearchParamsChange,
        getBaseSearchQueryParameters: getBaseSearchQueryParametersCallback,
    };
};
